import { sql } from 'drizzle-orm'
import { index, jsonb, pgEnum, pgTable, text, timestamp, uniqueIndex } from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { createdAt, primaryId, updatedAt } from './columns.ts'
import { offering } from './offerings.ts'

/** Which form produced the lead. Complete set — pgEnum values never leave. */
export const leadKind = pgEnum('lead_kind', [
  'CONTACT',
  'GRAPHICS_QUOTE',
  'RENTAL_INQUIRY',
  'STUDIO_INQUIRY',
  'PRODUCTION_INQUIRY',
  'CONSULTATION',
])

export const leadStatus = pgEnum('lead_status', [
  'NEW',
  'IN_PROGRESS',
  'QUOTED',
  'WON',
  'LOST',
  'SPAM',
])

/**
 * An inbound enquiry. One table for every form on the site — they differ in
 * which catalogue reference is set and what lands in `payload`, not in what a
 * lead *is*, and six near-identical tables would mean six panel screens.
 *
 * **Consent is not optional.** `privacyConsentAt` is NOT NULL and has no
 * default: a row cannot exist without recording the moment consent was given,
 * and `consentVersion` records which wording was shown. A staff member
 * entering a phone enquiry has to supply the timestamp they took it — which is
 * the legally correct behaviour, not an inconvenience. Marketing consent is
 * separate and genuinely optional.
 */
export const lead = pgTable(
  'lead',
  {
    id: primaryId('lead'),
    kind: leadKind('kind').notNull(),
    status: leadStatus('status').notNull().default('NEW'),

    // ── who ───────────────────────────────────────────────────────────────
    name: text('name'),
    email: text('email').notNull(),
    phone: text('phone'),
    companyName: text('company_name'),
    /** Set when a signed-in user submitted the form. */
    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),

    // ── what ──────────────────────────────────────────────────────────────
    subject: text('subject'),
    message: text('message'),
    /** What the enquiry is about — one key now that there is one catalogue. */
    offeringId: text('offering_id').references(() => offering.id, { onDelete: 'set null' }),
    /** Everything the form collected that the columns above do not model. */
    payload: jsonb('payload').$type<Record<string, unknown>>(),

    // ── where from ────────────────────────────────────────────────────────
    brand: text('brand'),
    locale: text('locale').notNull().default('pl'),
    /** Path the form was submitted from, e.g. `/pl/grafika`. */
    sourcePath: text('source_path'),
    referrer: text('referrer'),

    // ── consent (RODO) ────────────────────────────────────────────────────
    privacyConsentAt: timestamp('privacy_consent_at', { withTimezone: true }).notNull(),
    /** Identifier of the clause wording shown, e.g. `2026-07-privacy-v1`. */
    consentVersion: text('consent_version'),
    marketingConsentAt: timestamp('marketing_consent_at', { withTimezone: true }),

    // ── handling ──────────────────────────────────────────────────────────
    assignedToUserId: text('assigned_to_user_id').references(() => user.id, {
      onDelete: 'set null',
    }),
    internalNotes: text('internal_notes'),
    respondedAt: timestamp('responded_at', { withTimezone: true }),
    closedAt: timestamp('closed_at', { withTimezone: true }),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('lead_kind_idx').on(t.kind, t.createdAt),
    index('lead_status_idx').on(t.status, t.createdAt),
    index('lead_email_idx').on(t.email),
    index('lead_assigned_idx').on(t.assignedToUserId),
  ],
)

export const dataRequestKind = pgEnum('data_request_kind', [
  'ACCESS',
  'EXPORT',
  'RECTIFICATION',
  'ERASURE',
  'RESTRICTION',
  'OBJECTION',
  'PORTABILITY',
])

export const dataRequestStatus = pgEnum('data_request_status', [
  'OPEN',
  'VERIFYING',
  'IN_PROGRESS',
  'COMPLETED',
  'REJECTED',
])

/**
 * A GDPR/RODO subject request. Opens as `OPEN` and is worked by a human —
 * nothing in this system may delete or export personal data autonomously on
 * the strength of an unverified web form, which is why `verifiedAt` and
 * `handledByUserId` exist and why there is no automated transition to
 * `COMPLETED`.
 */
export const dataRequest = pgTable(
  'data_request',
  {
    id: primaryId('drq'),
    kind: dataRequestKind('kind').notNull(),
    status: dataRequestStatus('status').notNull().default('OPEN'),

    userId: text('user_id').references(() => user.id, { onDelete: 'set null' }),
    email: text('email').notNull(),
    message: text('message'),

    /** Set once the requester's identity was actually confirmed. */
    verifiedAt: timestamp('verified_at', { withTimezone: true }),
    handledByUserId: text('handled_by_user_id').references(() => user.id, { onDelete: 'set null' }),
    handledAt: timestamp('handled_at', { withTimezone: true }),
    resolutionNote: text('resolution_note'),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('data_request_status_idx').on(t.status, t.createdAt),
    index('data_request_email_idx').on(t.email),
    // At most one request of a kind open per address at a time: a refresh-spam
    // erasure request must not become five tickets for one person.
    uniqueIndex('data_request_open_unique')
      .on(t.email, t.kind)
      .where(sql`${t.status} IN ('OPEN', 'VERIFYING', 'IN_PROGRESS')`),
  ],
)

export type Lead = typeof lead.$inferSelect
export type NewLead = typeof lead.$inferInsert
export type DataRequest = typeof dataRequest.$inferSelect
